/**
 * The kinds of module an offer line can be.
 *
 * A module is a type plus, for the two gate families that have priced sub-kinds, a variant. The
 * variant is stored on `tip_poarta_mare`, which is where the gate kind has always lived, so no
 * saved offer has to be rewritten.
 *
 * Everything that used to branch on the type string branches on `moduleCategory()` instead, which
 * is what lets the retired types keep working: an offer saved as a `Poarta_mare` still reads,
 * lays out and prices exactly as it did, while new offers use the split list.
 */

export const MODULE_TYPES = [
	{ value: "Panou", label: "Panou", category: "panel" },
	{ value: "Poarta_mica", label: "Poarta pietonala", category: "pedestrian" },
	{ value: "Poarta_batanta", label: "Poarta auto batanta", category: "driveway" },
	{ value: "Poarta_culisanta", label: "Poarta auto culisanta", category: "driveway" },
	{ value: "Poarta_autoportanta", label: "Poarta auto autoportanta", category: "driveway" },
	{ value: "Stalp", label: "Stalp", category: "post" },
];

/**
 * Types that are not offered any more but still sit in saved offers. They stay selectable only
 * on a module that already carries one, so an old offer can be opened and edited without being
 * silently converted - and repriced - behind the user's back.
 */
export const LEGACY_MODULE_TYPES = [
	{ value: "Panou_rama", label: "Panou cu rama (vechi)", category: "panel" },
	{ value: "Poarta_mare", label: "Poarta auto (vechi)", category: "driveway" },
];

const SWING_VARIANTS = [
	{ value: "batanta_simpla", label: "1 canat" },
	{ value: "batanta_dubla", label: "2 canate" },
	{ value: "batanta_simpla_bifold", label: "Bi-fold 2 canate" },
	{ value: "batanta_dubla_bifold", label: "Bi-fold 4 canate" },
];

const SLIDE_VARIANTS = [
	{ value: "culisanta", label: "1 canat" },
	// the configurator has no separate column for a two-leaf slider, so it prices as a slider and
	// differs only in how much wider than its opening it has to be
	{ value: "culisanta_dubla", label: "2 canate" },
	{ value: "culisanta_telescopica", label: "Telescopica" },
	{ value: "culisanta_articulata", label: "Articulata" },
];

export const MODULE_VARIANTS = {
	Poarta_batanta: SWING_VARIANTS,
	Poarta_culisanta: SLIDE_VARIANTS,
	// the retired catch-all kept every gate kind under one type, so an old offer needs the lot
	Poarta_mare: SWING_VARIANTS.concat(SLIDE_VARIANTS, [
		{ value: "autoportanta", label: "Autoportanta" }
	]),
};

/**
 * The model code standing for "not one of the twenty".
 *
 * An agent picks it when a client wants a design the catalogue does not cover, and then states
 * the direction and the slat width themselves. It carries no adjustment, so a custom module is
 * priced at its country's base price for the type.
 */
export const CUSTOM_MODEL = "CUSTOM";

export function isCustomModel(code) {
	return code == CUSTOM_MODEL;
}

/**
 * What a custom module is called: its direction and its slat width, the way the real codes are
 * named. "H87.4", "V60". Also used as its effective code, so the geometry rules that read a code
 * treat it exactly like the catalogue model it resembles.
 */
export function customModelCode(orientation, width) {
	return orientation && width > 0 ? `${orientation}${Number(width)}` : "";
}

/** Post cross-sections, matching the price_post_* columns. */
export const POST_SIZES = [80, 100, 200];

/**
 * Frame thickness in mm, per side. It used to be a select because other thicknesses were planned,
 * but 70 is the only one ever built, so it is a constant rather than a one-option dropdown. The
 * frame is subtracted from both sides of the span before the slats are laid out.
 */
export const FRAME_THICKNESS = 70;

const CATEGORIES = MODULE_TYPES.concat(LEGACY_MODULE_TYPES).reduce((all, type) => {
	all[type.value] = type.category;
	return all;
}, {});

/** panel | pedestrian | driveway | post, or null for a module with no type yet. */
export function moduleCategory(tip) {
	return CATEGORIES[tip] || null;
}

export function variantsFor(tip) {
	return MODULE_VARIANTS[tip] || [];
}

export function moduleTypeLabel(tip) {
	const type = MODULE_TYPES.concat(LEGACY_MODULE_TYPES).find(item => item.value == tip);

	return type ? type.label : tip;
}

/** Everything but a plain panel and a post is built inside a frame, which eats into the span. */
export function hasFrame(tip) {
	const category = moduleCategory(tip);

	return !!category && tip != "Panou" && category != "post";
}

/** Only driveway gates can carry a reinforcement profile. */
export function hasReinforcement(tip) {
	return moduleCategory(tip) == "driveway";
}

/** A reinforcement is the 70x40 frame profile, and takes its face out of the slat run. */
export const REINFORCEMENT_WIDTH = FRAME_THICKNESS;

/**
 * Production's rule for vertical reinforcement on a vertical model: none up to 1700 mm of leaf,
 * then one more for every further 1700 - a 1750 leaf takes 1, a 3450 leaf 2. Only a suggestion:
 * how a gate is reinforced is decided on the module, and the card warns when it falls short.
 */
export const VERTICAL_REINFORCEMENT_SPACING = 1700;

export function suggestedVerticalReinforcements(leafWidth) {
	const width = Number(leafWidth) || 0;

	return width > VERTICAL_REINFORCEMENT_SPACING
		? Math.ceil(width / VERTICAL_REINFORCEMENT_SPACING) - 1
		: 0;
}

/**
 * The part of an image filename that says what the module is.
 *
 * Where a gate has a variant the variant already names the kind - "batanta_simpla" says both that
 * it swings and that it has one leaf - so it stands alone. Everything else is named by its type,
 * shortened where the type string carries a "poarta_" prefix the other names do not, so the whole
 * set reads the same way. No digits: they dated from when every driveway gate shared one type and
 * the number was the only thing telling them apart.
 */
const IMAGE_KINDS = {
	Poarta_mica: "pietonala",
	Poarta_autoportanta: "autoportanta",
};

export function imageKind(tip, variant) {
	if (variant) return variant;

	return IMAGE_KINDS[tip] || String(tip || "").toLowerCase();
}

/**
 * The size to quote for a module, as "4450 x 1880".
 *
 * Documents describe what gets built, which is the module the card derived - `latime`/`inaltime`
 * hold the opening that was measured, and for a slider or a gate with clearances the two differ.
 * A module saved before the card derived anything carries its size directly, so that is the
 * fallback rather than printing blanks on an older offer or contract.
 */
export function moduleSizeText(modul) {
	if (!modul) return "";

	return modul.modul_latime
		? `${modul.modul_latime} x ${modul.modul_inaltime}`
		: `${modul.latime} x ${modul.inaltime}`;
}

/**
 * A post's height. It has no width to quote - its cross-section is already part of its name - so a
 * document states the height alone rather than a size with an empty side.
 */
export function moduleHeightText(modul) {
	if (!modul) return "";

	return String(modul.modul_inaltime || modul.inaltime || "");
}

/**
 * The translation key naming what a module is, for the offer and the PDF.
 *
 * Keyed off the same type and variant the rest of the card works from, so a new variant needs a
 * line here and a line in each language file - never a chain of country checks in a component.
 * The retired types map onto their modern equivalents: an old Poarta_mare carries the gate kind
 * on its variant, exactly as the split types do.
 */
const MODULE_NAME_KEYS = {
	batanta_simpla: "swingGateSingleLeaf",
	batanta_dubla: "swingGateDoubleLeaf",
	batanta_simpla_bifold: "swingGateBiFoldSingle",
	batanta_dubla_bifold: "swingGateBiFoldDouble",
	culisanta: "slidingGateSingleLeaf",
	culisanta_dubla: "slidingGateDoubleLeaf",
	culisanta_telescopica: "slidingGateTelescopic",
	culisanta_articulata: "slidingGateArticulated",
	autoportanta: "selfSupportingGate",
};

const TYPE_NAME_KEYS = {
	Panou: "panel",
	Panou_rama: "framedPanel",
	Poarta_mica: "pedestrianGate",
	Poarta_autoportanta: "selfSupportingGate",
	Stalp: "posts",
};

const POST_NAME_KEYS = { 80: "post80", 100: "post100", 200: "post200" };

export function moduleNameKey(tip, variant, postSize) {
	if (tip == "Stalp") return POST_NAME_KEYS[postSize] || "posts";
	if (variant && MODULE_NAME_KEYS[variant]) return MODULE_NAME_KEYS[variant];

	return TYPE_NAME_KEYS[tip] || null;
}

/** A post is a piece of stock: no slats, no area, no thresholds. */
export function isPost(tip) {
	return moduleCategory(tip) == "post";
}

/**
 * How the opening an agent measures becomes the module that gets built.
 *
 * Width: a panel and a swing gate fill their opening, but a slider has to run past it and a
 * cantilever needs its tail, so those carry extra width. The figure depends on the variant, not
 * only the type, because a two-leaf slider overlaps twice.
 *
 * Height: every gate needs clearance to swing or roll, so its leaf is shorter than the opening by
 * the gaps the agent enters. Panels and posts have none.
 *
 * `null` means the adjustment is not known yet rather than zero - the card says so instead of
 * quoting a figure nobody has confirmed.
 */
const WIDTH_ADJUSTMENTS = {
	Panou: 0,
	Poarta_mica: 0,
	Poarta_batanta: 0,
	Poarta_autoportanta: 350,
	Stalp: 0,
};

const SLIDE_WIDTH_ADJUSTMENTS = {
	culisanta: 450,
	culisanta_dubla: 700,
	culisanta_telescopica: 750,
	// from the client's calculation workbook, which is the only figure there is for it
	culisanta_articulata: 1100,
};

/**
 * Everything but a post carries a gap above and below: a gate needs it to swing or roll clear, a
 * panel to sit off the ground. What the two gaps shorten differs - see moduleAssembly().
 */
export function hasClearanceGaps(tip) {
	return !!moduleCategory(tip) && moduleCategory(tip) != "post";
}

/**
 * Extra width this module needs beyond its opening, in mm. `null` when it is not defined yet.
 */
export function widthAdjustment(tip, variant) {
	if (tip == "Poarta_culisanta") {
		const adjustment = SLIDE_WIDTH_ADJUSTMENTS[variant];

		// no variant picked yet reads as nothing to add, not as unknown
		return variant === null || variant === undefined || variant === "" ? 0 : adjustment;
	}

	// the retired catch-all carried the slider kinds on its variant
	if (tip == "Poarta_mare" && variant in SLIDE_WIDTH_ADJUSTMENTS) {
		return SLIDE_WIDTH_ADJUSTMENTS[variant];
	}
	if (tip == "Poarta_mare") {
		return variant == "autoportanta" ? 350 : 0;
	}

	const adjustment = WIDTH_ADJUSTMENTS[tip];

	return adjustment === undefined ? 0 : adjustment;
}

/**
 * How a module is put together across its width: a post each side, a clearance between each post
 * and the leaf, the leaf itself, and a gap wherever two leaves meet.
 *
 *   [ post ][ start ][ leaf ][ join ][ leaf ][ end ][ post ]
 *
 * The two side clearances are not always equal - a pedestrian gate is 15 on one side and 10 on the
 * other - and the join is wider than the sides on a two-leaf swing, where the leaves meet in the
 * middle. A panel has no posts and a slider runs past its opening instead of sitting between
 * posts, so neither appears here; a type absent from this table has no breakdown and its leaf is
 * the whole module.
 */
const ASSEMBLIES = {
	Poarta_mica: { post: 70, gapStart: 15, gapEnd: 10, join: 15 },
	Poarta_batanta: { post: 70, gapStart: 15, gapEnd: 15, join: 15 },
};

/** Where two leaves meet is wider than the standard join on a two-leaf swing. */
const JOIN_OVERRIDES = {
	batanta_dubla: 20,
};

const LEAF_COUNTS = {
	batanta_simpla: 1,
	batanta_dubla: 2,
	batanta_simpla_bifold: 2,
	batanta_dubla_bifold: 4,
};

/** The resolved assembly for a module, join width included. Null where none is defined. */
export function assemblyFor(tip, variant) {
	const base = ASSEMBLIES[tip];

	if (!base) return null;

	const override = JOIN_OVERRIDES[variant];

	return Object.assign({}, base, {
		join: override === undefined ? base.join : override
	});
}

/** How many leaves the gate closes with. Anything not a multi-leaf swing is a single leaf. */
export function leafCount(variant) {
	return LEAF_COUNTS[variant] || 1;
}

/**
 * Sliders that are built as two frames, though they close as one gate and lay their slats out as
 * one run. From the client's workbook; hub API GateAssembly keeps the same list.
 */
const SLIDE_FRAMES = {
	culisanta_dubla: 2,
	culisanta_telescopica: 2,
};

/** How many framed leaves the gate is built from. */
export function framedLeafCount(variant) {
	return SLIDE_FRAMES[variant] || leafCount(variant);
}

/**
 * The parts a module breaks down into, all in mm.
 *
 * Height. Both gaps are dead space inside the opening, so both come off the module, whatever the
 * type. The posts are the exception: they stand on the ground and so give up only the gap above,
 * which is what leaves a swing or pedestrian gate's posts taller than the module between them.
 *
 * Width. The module is the opening plus whatever the gate needs past it. Where the assembly is
 * known, the leaf gives up a post and a clearance on each side, and one inter-leaf gap for every
 * join between leaves. Those joins only ever change the layout of a vertical model, since a
 * horizontal one lays its slats over the height.
 *
 * @returns { module, leaf, postHeight, leaves, adjustment, known, unknown }
 */
export function moduleAssembly(tip, variant, openingWidth, openingHeight, gaps) {
	const assembly = assemblyFor(tip, variant);
	const adjustment = widthAdjustment(tip, variant);
	const width = Number(openingWidth) || 0;
	const height = Number(openingHeight) || 0;
	const top = hasClearanceGaps(tip) ? Number(gaps && gaps.top) || 0 : 0;
	const bottom = hasClearanceGaps(tip) ? Number(gaps && gaps.bottom) || 0 : 0;

	const module = {
		width: width + (adjustment || 0),
		// Neither gap is part of the module: the one above is measured down from the upper limit,
		// the one below holds the module off the ground. Only the posts span the lower gap, and
		// postHeight below is where that shows.
		height: height - top - bottom
	};

	const leaves = leafCount(variant);
	const sides = assembly ? 2 * assembly.post + assembly.gapStart + assembly.gapEnd : 0;
	const joins = assembly ? (leaves - 1) * assembly.join : 0;

	return {
		module: module,
		leaf: {
			width: module.width - sides - joins,
			height: height - top - bottom
		},
		postHeight: assembly ? height - top : null,
		leaves: leaves,
		// the width this type needs beyond its opening, 0 where it fills the opening exactly
		adjustment: adjustment || 0,
		known: !!assembly,
		unknown: adjustment === null
	};
}
